import { useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function getInitialDate(value: string) {
  if (value) {
    const parsedDate = new Date(value);

    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return new Date();
}

function moveMonth(year: number, month: number, direction: -1 | 1) {
  const nextDate = new Date(year, month + direction, 1);

  return {
    year: nextDate.getFullYear(),
    month: nextDate.getMonth(),
  };
}

function getWeekdayColor(weekday: number) {
  if (weekday === 0) {
    return '#dc2626';
  }

  if (weekday === 6) {
    return '#2563eb';
  }

  return '#94a3b8';
}

function getDateTextColor(isSelected: boolean, weekday: number) {
  if (isSelected) {
    return '#fff';
  }

  if (weekday === 0) {
    return '#dc2626';
  }

  if (weekday === 6) {
    return '#2563eb';
  }

  return '#334155';
}

function getDateBorder(isSelected: boolean, isToday: boolean) {
  if (!isSelected && isToday) {
    return '1px solid #bfe3fb';
  }

  return '1px solid transparent';
}

function getDateHoverBackground(isSelected: boolean) {
  if (isSelected) {
    return '#1791db';
  }

  return '#f1f5f9';
}

function CalendarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

type DatePickerFieldProps = Readonly<{
  value: string; // 'yyyy-mm-dd' 또는 ''
  onChange: (value: string) => void;
  placeholder?: string;
  fullWidth?: boolean;
}>;

type CalendarCell = Readonly<{
  key: string;
  day: number | null;
  weekday: number;
}>;

function DatePickerField({ value, onChange, placeholder, fullWidth }: DatePickerFieldProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const initialMonth = () => {
    const base = getInitialDate(value);
    return { year: base.getFullYear(), month: base.getMonth() };
  };
  const [cursor, setCursor] = useState(initialMonth);

  const openPopover = (e: MouseEvent<HTMLElement>) => {
    setCursor(initialMonth()); // 열 때마다 현재 값의 달로 맞춤
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const startWeekday = new Date(cursor.year, cursor.month, 1).getDay();
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const dateKey = (day: number) => `${cursor.year}-${pad(cursor.month + 1)}-${pad(day)}`;

  const cells: CalendarCell[] = [];
  for (let i = 0; i < startWeekday; i += 1) {
    cells.push({ key: `blank-${cursor.year}-${cursor.month}-${i}`, day: null, weekday: i });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    const cellIndex = startWeekday + d - 1;
    cells.push({ key: dateKey(d), day: d, weekday: cellIndex % 7 });
  }

  const today = todayKey();

  const prevMonth = () =>
    setCursor((c) => moveMonth(c.year, c.month, -1));
  const nextMonth = () =>
    setCursor((c) => moveMonth(c.year, c.month, 1));

  const pick = (day: number) => {
    onChange(dateKey(day));
    close();
  };

  return (
    <>
      <TextField
        value={value}
        placeholder={placeholder ?? '날짜 선택'}
        size="small"
        fullWidth={fullWidth}
        onClick={openPopover}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Box sx={{ color: '#94a3b8', display: 'flex', ml: 0.5 }}>
              <CalendarIcon />
            </Box>
          ),
        }}
        sx={{
          '& .MuiInputBase-root': { cursor: 'pointer' },
          '& input': { cursor: 'pointer', fontSize: 14 },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, border: '1px solid #e8edf3' } } }}
      >
        <Box sx={{ p: 1.5, width: 268 }}>
          {/* 월 이동 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <IconButton size="small" aria-label="이전 달" onClick={prevMonth}>‹</IconButton>
            <Box sx={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
              {cursor.year}년 {cursor.month + 1}월
            </Box>
            <IconButton size="small" aria-label="다음 달" onClick={nextMonth}>›</IconButton>
          </Box>

          {/* 요일 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
            {WEEKDAYS.map((w, i) => (
              <Box
                key={w}
                sx={{
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  color: getWeekdayColor(i),
                }}
              >
                {w}
              </Box>
            ))}
          </Box>

          {/* 날짜 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {cells.map((cell) => {
              if (cell.day == null) return <Box key={cell.key} />;
              const day = cell.day;
              const key = dateKey(day);
              const isSelected = key === value;
              const isToday = key === today;

              return (
                <Box
                  key={key}
                  onClick={() => pick(day)}
                  sx={{
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1.5,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    color: getDateTextColor(isSelected, cell.weekday),
                    backgroundColor: isSelected ? '#1da1f2' : 'transparent',
                    border: getDateBorder(isSelected, isToday),
                    '&:hover': { backgroundColor: getDateHoverBackground(isSelected) },
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default DatePickerField;
